import React, {
    useState, useEffect, useCallback, useMemo,
} from 'react';
import { isEmpty, isEqual } from 'lodash';
import { NSCard, NSDropdown, NSInputNumberStepper } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { useUpdateProForma } from 'queries/ProForma';
import { Tooltip } from 'reactstrap';
import RenderIf from 'components/RenderIf/RenderIf';
import { useQueryClient } from '@tanstack/react-query';
import './Sensitivity.scss';
import { useSensitivityStorage } from './hooks/useSensitivityStorage';
import SensitivityTableContainer from './SensitivityTableContainer';
import DeleteSensitivityConfirmationModal from './DeleteSensitivityConfirmationModal/DeleteSensitivityConfirmationModal';
import {
    getSensitivityOptions,
    getSensitivityIdFromType,
    getSensitivityTypeFromId,
} from './constants';
import { useProFormaSocketContext } from '../ProForma/socketContext/ProFormaSocketProvider';

export interface ISensitivityProps {
    hasWriteAccessToProForma: boolean;
    dealId: number;
    proFormaId: number;
}

interface TableConfig {
    id: string;
    type: string;
    tableLabel: string;
}

const Sensitivity = ({
    hasWriteAccessToProForma, dealId, proFormaId,
}: ISensitivityProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const queryClient = useQueryClient();
    const { proForma, returnsData } = useProFormaSocketContext();

    const toggleTooltip = () => setShowTooltip(prev => !prev);
    const proFormaStatusId = proForma?.proFormaStatusId;
    const isInProgress = proFormaStatusId === PRO_FORMA_STATUS.IN_PROGRESS;
    const isDraft = proFormaStatusId === PRO_FORMA_STATUS.DRAFT;
    const proFormaInProgress = isInProgress || isDraft;

    const defaultTables = useMemo(() => [{
        id: 'main',
        type: 'Exit Capitalization Rate vs Gross Rent',
        tableLabel: 'Exit cap rate vs Gross Rent',
    }], []);

    const {
        saveSensitivity,
        removeSensitivity,
        loadSensitivities,
        clearSensitivities,
    } = useSensitivityStorage(String(proFormaId), proForma?.proFormaModelId);

    const [tables, setTables] = useState<TableConfig[]>(defaultTables);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [tableToDelete, setTableToDelete] = useState('');

    const { mutate: updateProForma } = useUpdateProForma();
    const handleUpdateProForma = useCallback((patchData: Object) => {
        updateProForma(
            { dealId: Number(dealId), proFormaId: Number(proFormaId), patchData },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ['proFormaData'] });
                    queryClient.invalidateQueries({ queryKey: ['ProFormaSensitivityAnalysis'] });
                },
            },
        );
    }, [updateProForma, dealId, proFormaId, queryClient]);

    useEffect(() => {
        const loadedTables = loadSensitivities();
        if (loadedTables.length > 0) {
            setTables(prevTables => {
                // Only update if the tables are actually different
                if (!isEqual(prevTables, loadedTables)) return loadedTables;
                return prevTables;
            });
        }
    }, [proFormaId, proForma?.proFormaModelId]);

    useEffect(() => {
        if (proFormaInProgress) {
            clearSensitivities();
            setTables(defaultTables);
        }
    }, [proFormaInProgress, clearSensitivities, defaultTables]);

    // Set initial target IRR if not set and returns data is available
    useEffect(() => {
        if (returnsData?.netLeveredIrr && !proForma?.targetLeveredIrr) {
            handleUpdateProForma({
                targetLeveredIrr: returnsData?.netLeveredIrr,
            });
        }
    }, [returnsData?.netLeveredIrr, proForma?.targetLeveredIrr, handleUpdateProForma]);

    // Save/remove sensitivities when tables change
    useEffect(() => {
        const currentIds = tables.map(table => getSensitivityIdFromType(table.type)).filter(Boolean) as string[];
        const loadedSensitivities = loadSensitivities();
        const savedIds = loadedSensitivities.map(table => getSensitivityIdFromType(table.type)).filter(Boolean) as string[];

        // Add new sensitivities with default increments
        currentIds.forEach(id => {
            if (!savedIds.includes(id)) {
                const sensitivityType = getSensitivityTypeFromId(id);
                const options = getSensitivityOptions(proForma?.proFormaModelId);
                const option = options.find(opt => opt.value === sensitivityType);
                const defaultIncrements = option?.increments || { vertical: 1, horizontal: 0.01 };
                saveSensitivity(id, defaultIncrements.vertical, defaultIncrements.horizontal);
            }
        });

        // Remove sensitivities that are saved but no longer in current tables
        savedIds.forEach(id => {
            if (!currentIds.includes(id)) removeSensitivity(id);
        });
    }, [tables, saveSensitivity, removeSensitivity, loadSensitivities, proForma?.proFormaModelId]);

    const handleSelectSensitivity = (option: IOptionAnyValue) => {
        if (option.value !== 'default') {
            const selectedOption = getSensitivityOptions(proForma?.proFormaModelId).find(opt => opt.value === option.value);
            if (selectedOption) {
                const newTable = {
                    id: `table-${Date.now()}`,
                    type: option.value as string,
                    tableLabel: option.label,
                };
                const updatedTables = [...tables, newTable];
                setTables(updatedTables);
            }
        }
    };

    const handleDeleteSensitivityConfirm = (tableId: string) => {
        setTableToDelete(tableId);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteSensitivity = () => {
        const updatedTables = tables.filter(table => table.id !== tableToDelete);
        setTables(updatedTables);
        setTableToDelete('');
        setIsDeleteModalOpen(false);
    };

    const selectedTableTypes = new Set(tables.map(table => table.type));
    const filteredSensitivityOptions = getSensitivityOptions(proForma?.proFormaModelId).filter(option => !selectedTableTypes.has(option.value));

    return (
        <RenderIf isTrue={!isEmpty(proForma)} isLoading={isEmpty(proForma)}>
            <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma}>
                <NSCard className="NSCard--level-3 d-flex flex-row justify-content-between align-items-center mb-2 p-2">
                    <div>
                        <h4 className="text-white font-weight-bold mb-0 mt-0">Sensitivity analysis</h4>
                        <div>Set your Target Levered IRR to help generate analysis charts</div>
                    </div>
                    <div className="d-flex align-items-center">
                        <span className="text-white font-weight-bold mr-2">Target Levered IRR:</span>
                        <NSInputNumberStepper
                            percentage
                            name="targetLeveredIrr"
                            value={proForma?.targetLeveredIrr ? Number(proForma.targetLeveredIrr) : null}
                            min={0}
                            step={0.1}
                            onChange={(value: number) => handleUpdateProForma({
                                targetLeveredIrr: value,
                            })}
                            inputGroupClassName="Sensitivity--input-width"
                        />
                    </div>
                </NSCard>

                {tables.map(table => (
                    <SensitivityTableContainer
                        key={table.id}
                        dealId={dealId}
                        proFormaId={proFormaId}
                        tableConfig={table}
                        onRequestDelete={handleDeleteSensitivityConfirm}
                        proFormaInProgress={proFormaInProgress}
                    />
                ))}

                <RenderIf isTrue={filteredSensitivityOptions.length}>
                    <NSCard>
                        <div className="Sensitivity__add-sensitivity">
                            <div className="Sensitivity__add-sensitivity__content">
                                <div className="Sensitivity__add-sensitivity__title pb-1">Add sensitivity</div>
                                <RenderIf isTrue={proFormaInProgress}>
                                    <Tooltip target="dropdown-tooltip-wrapper" isOpen={showTooltip} toggle={toggleTooltip} autohide={false}>
                                        Your pro forma must be substantially complete to generate sensitivities
                                    </Tooltip>
                                </RenderIf>
                                <div className="Sensitivity__add-sensitivity__dropdown" id="dropdown-tooltip-wrapper">
                                    <NSDropdown
                                        options={filteredSensitivityOptions}
                                        placeholder="Select sensitivity"
                                        handleSelectOption={handleSelectSensitivity}
                                        isFullWidth
                                        disabled={!hasWriteAccessToProForma || proFormaInProgress}
                                    />
                                </div>
                            </div>
                        </div>
                    </NSCard>
                </RenderIf>

                <DeleteSensitivityConfirmationModal
                    isOpen={isDeleteModalOpen}
                    toggle={() => setIsDeleteModalOpen(!isDeleteModalOpen)}
                    handleDelete={handleDeleteSensitivity}
                />
            </ReadOnlyWrapper>
        </RenderIf>
    );
};

export default Sensitivity;
